import React from 'react';
import _ from 'lodash';
import { lowerCase, isEmpty, isFunction, capitalize, isNaN } from 'lodash-es';
import { Button, Popover, Tag, Tooltip, Typography, Progress } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import dayjs from 'dayjs';
import { SEVERITY_COLOR, PROGRESS_COLOR, COLORS } from 'constants/Layout';
import { NOT_AVAILABLE } from 'ac-components/ac-na-component/ac-na-component';
import { VOCABULARY_STATUS, GLOBAL_STATUS } from 'views/Findings/constants/status';
import { SEVERITY_NAMES } from 'views/Findings/constants/severity';
import { getVocabulary } from 'components/Vocabulary';
import ContentOverlay from 'components/ContentOverlay';
import NameAvatar from 'components/NameAvatar';
import FilterableCell from 'components/FilterableCell';
import { marked } from 'marked';
import parseHtml from 'utils/parserUtil';
import { transformComment } from 'utils/commentUtil';
import { startCaseWithSpace } from 'utils/table/utils/startCaseWithSpace';
import { formatNumber } from 'utils/table/utils/formatNumber';
import { RelativeTimeComponent } from 'utils/table/components/RelativeTimeComponent';

const {Text} = Typography;

function GenericMoreItemsRenderer(values = [], className = '', Component, delimiter = ','){
    if(_.isEmpty(values)){
        return 'N/A';
    }
    if(typeof values === 'string'){
        values = [values];
    }
    const resources = values.slice();
    const minResources = resources.splice(0, 2);
    let content = null;
    if(resources.length){
        content = (
            <div style={{maxHeight: 150, maxWidth: 200, overflow: 'auto'}}>
                {
                    values.map((r, i) => [i !== 0 ? delimiter : '', <Component style={{marginRight: 2, marginBottom: 2}} className={className} key={i}>{r.name || r}</Component>])
                }
            </div>
        )
    }
    return (
        <div>
            {
                minResources.map((r, i) => [i !== 0 ? delimiter : '', <Component index={i} title={r.name || r} className= {classNames('text-ellipsis tools', className)} style={{marginRight: 2, marginBottom: -2, maxWidth: '100%', display: 'inline-block',fontSize: 13}} key={i}>{r.name || r}</Component>])
            }
            {
                resources.length
                ?
                <Popover content={content}>
                    <Button size="small" type="link" style={{fontSize: 12}}>+{resources.length}</Button>
                </Popover>
                :
                null
            }
        </div>
    )
}

function PlusMoreSingleLineRenderer({ values, limit = 1, className = undefined, Component = 'span', delimiter=', ', style = {}, getExtraComponentProps = undefined}){
    if(_.isEmpty(values)){
        return CellRenderers.VALUE_OR_NA();
    }
    if(_.isString(values)){
        values = [values];
    }
    const resources = _.compact(values.slice());
    const minResources = resources.splice(0, limit);
    let content = null;
    if(resources.length){
        content = (
            <div style={{maxHeight: 150, maxWidth: 200, overflow: 'auto'}}>
                {
                    resources.map((r, i) => [i !== 0 ? delimiter : '', <Component style={{marginRight: 2, marginBottom: 2}} className={className} key={i} {...(isFunction(getExtraComponentProps) ? getExtraComponentProps(r) : {})}>{r.name || r}</Component>])
                }
            </div>
        )
    }
    return (
        <div className="d-flex align-items-center">
            <div className="text-ellipsis" title={_.join(minResources, delimiter)}>
                {
                    minResources.map((r, i) => [i !== 0 ? delimiter : '', <Component index={i} className= {classNames(className)} style={{...{marginRight: 2, marginBottom: -2, maxWidth: '100%', fontSize: 13}, ...style}} key={i} {...(isFunction(getExtraComponentProps) ? getExtraComponentProps(r) : {})}>{r.name || r}</Component>])
                }
            </div>
            {
                resources.length
                ?
                <Popover content={content}>
                    <Button size="small" type="link" style={{fontSize: 12}}>+{resources.length}</Button>
                </Popover>
                :
                null
            }
        </div>
    )
}

function findingTags({ values, limit = 1, className, maxWidth = 200, filterKey, getFilterValue,findingId, showFilter=true }) {
    if (_.isEmpty(values)) {
        return CellRenderers.VALUE_OR_NA();
    }
    if (typeof values === 'string') {
        values = [values];
    }
    const resorces = values.slice();
    const minResources = resorces.splice(0, limit);
    let content = null;

    const tagContent = (text) => {
        return (
            <FilterableCell showFilter={showFilter} className="d-flex align-items-center" key={text} filterKey={filterKey} value={text} getFilterValue={getFilterValue}>
                {filterKey === 'cve' ? <a className="text-ellipsis" rel="noopener noreferrer" target="_blank"  href={`/compliance/vulnerability-rules/${text}/redirect?findingId=${findingId}`}>{text}</a> : <span className="text-ellipsis">{text}</span>}
            </FilterableCell>
        )
    };
    
    if (resorces.length) {
        content = (
            <div style={{ maxHeight: 150, maxWidth: 200, overflow: 'auto' }}>
                {
                    _.map(values, (text, i) => <>{i !==0 ? ',' : ''} {_.isEmpty(text) ? CellRenderers.VALUE_OR_NA() :
                    <Tag style={{ marginRight: 2, marginBottom: 2 }} className={classNames(className)} key={`${text}-${i}`}>
                        {tagContent(text)}
                    </Tag>}</>)
                }
            </div>
        )
    }
    return (
        <div style={{verticalAlign: 'middle'}}>
            {
                _.map(minResources, (text, i) => _.isEmpty(text) ? CellRenderers.VALUE_OR_NA() :
                    <Tag title={text} className={classNames('text-ellipsis tools', className)} style={{ marginRight: 2, verticalAlign: 'inherit', maxWidth, fontSize: 13 }}>
                        {tagContent(text)}
                    </Tag>
                )
            }
            {
                resorces.length
                    ?
                    <Popover content={content}>
                        <Typography.Link className="p-l-0" style={{ fontSize: 12, verticalAlign: 'middle' }}>+{resorces.length}</Typography.Link>
                    </Popover>
                    :
                    null
            }
        </div>
    )
}

function SpanComponent(props){
    return <span {...props} style={props.index === 0 ? { ...props.style } : { ...props.style, marginLeft: 5 }} />
}

function MoreItemsRenderer(values, className){
    return GenericMoreItemsRenderer(values, className, SpanComponent);
}

function TagsRenderer(values, className){
    return GenericMoreItemsRenderer(values, className, Tag);
}

function CustomizedSeverityRenderer(severity = '', className = ''){
    if(_.isEmpty(severity)){
        return '';
    }
    if(!SEVERITY_NAMES[severity.toLowerCase()]){
        return severity;
    }
    return <Tag style={{ maxWidth: '100%', paddingInline: 8 }} className={`${severity.toLowerCase()} severity-width text-ellipsis ${className}`} title={getVocabulary(severity)}>{getVocabulary(severity)}</Tag>
}

function SeverityRenderer(severity = '', className = '') {
        if(_.isEmpty(severity)){
            return '';
        }
        if(!SEVERITY_NAMES[severity.toLowerCase()]){
            return severity;
        }
        return <Tag className={`${severity.toLowerCase()} severity-width ${className}`}>{startCaseWithSpace(severity)}</Tag>
}


const getTagColor = (status) =>{
    let color;
    switch(status){
        case 'ACTIVE':
            color = 'activeStatus'
            break;
        case 'INACTIVE':
            color = 'info'
            break;
        case 'PARTIAL':
            color = 'partial'
            break;
        case 'NOT_CONFIGURED':
            color = 'medium'
            break;
        case 'EXCLUDED':
            color = 'excluded'
            break;
        default :
           color = 'critical'
    }
    return color;
}

function operationalStatusRenderer(status = '', record = {}) {
    const { operationalStatusReason , operationalStatusMessage} = record;
    if(_.isEmpty(status)){
        return '';
    }
    return <Tooltip title={operationalStatusReason || operationalStatusMessage}>
                <Tag className={`${getTagColor(status)} ${status} break-content`}>{status === 'CONFIG_ERROR' ? startCaseWithSpace('Error') : startCaseWithSpace(status)}</Tag>
           </Tooltip>;
}

function scanStatusRenderer(status = ''){
    if(_.isEmpty(status)){
        return 'N/A';
    }
    let className;
    switch(status)
    {
        case 'PASSED':
            className='activeStatus'
            break;
        case 'FAILED':
            className='critical'
            break;
        case 'RUNNING':
            className='running'
            break;
        case 'PUBLISHED':
            className = 'medium'
            status = 'INQUEUE' 
            break;
        default:
            className='medium'   

    }
    return (
        status ==='INQUEUE' ? (
            <Tooltip title="Report request has been received by the ArmorCode job scheduler. It will be processed shortly.">
                <Tag className={`${className} severity-width`}>{startCaseWithSpace(status)}</Tag>
            </Tooltip>
        ) : (
            <Tag className={`${className} severity-width`}>{startCaseWithSpace(status)}</Tag>
        )
    );
}

function runbookStatusRenderer(status = ''){
    if(_.isEmpty(status)){
        return 'N/A';
    }
    let className = 'red';
    switch(status)
    {
        case 'COMPLETED':
            className='activeStatus'
            break;
        case 'FAILED':
            className='critical'
            break;
        case 'STOPPED':
        case 'CANCELLED':
            className='high'
            break;
        case 'STARTED':
            className='running'
            break;
        case 'CREATED':
            className='info'
            break;
        default:
            className='info'   

    }
    return <Tag className={className} style={{ width: '75px' }} >{startCaseWithSpace(status)}</Tag>
}

function scanTooltip(displayLetter,count,finding,record,onClick=_.noop,findingName){
    let _onClick;
    const disableClick = displayLetter === 'T' || displayLetter === 'D';
    if(count && record.scanStatus === 'COMPLETED' && !disableClick) {
        _onClick = () => onClick(record.id, findingName, record)
    }
    return (<Tooltip color={'white'} title={(<span className="" style={{backgroundColor:'white'}}>
    {(<Text className="critical">{`${getVocabulary('critical')} : ${finding?.severity?.Critical ?? 0}`}</Text>) || 0}<br/>
    {<Text className="high">{`${getVocabulary('high')} : ${finding?.severity?.High ?? 0}`}</Text>}<br/>
    {<Text className="medium">{`${getVocabulary('medium')} : ${finding?.severity?.Medium ?? 0}`}</Text>}<br/>
    {<Text className="low-text">{`${getVocabulary('low')} : ${finding?.severity?.Low ?? 0}`}</Text>}<br/>
    {<Text className="info">{`${getVocabulary('info')} : ${finding?.severity?.Info ?? 0}`}</Text>}
    </span>)} > 
    <Tag className={classNames('scan-finding',{ clickable: !!_onClick })} onClick={_onClick}> {displayLetter} <Text className="finding-text">{(count) ? formatNumber({count: +count, showTooltip: false}) : '-'}</Text></Tag>
    </Tooltip>)
}

function TotalFindingRenderer(finding = {},record,onClick){
    return (<>
    {scanTooltip('T',(finding.totalFindings && finding.totalFindings.count) || 0,finding.totalFindings, record, onClick, 'total')}
    {scanTooltip('D',(finding.duplicateFindings && finding.duplicateFindings.count) || 0,finding.duplicateFindings, record, onClick, 'duplicate')}
    {scanTooltip('R',(finding.resolvedFindings && finding.resolvedFindings.count) || 0, finding.resolvedFindings, record, onClick, 'resolved')}
    {scanTooltip('N',(finding.newFindings && finding.newFindings.count) || 0, finding.newFindings, record, onClick, 'new')}
    </>)
}

export function SubProductRenderer({name, productName}) {
    return (
        <div className="d-flex flex-column align-items-flex-start">
            <Text className="m-b-xs">{name}</Text>
            <Tag>{productName}</Tag>
        </div>
    );
}

export function TotalFindings(severity = {}, showMedium = true, showLow = true, hideNull = false) {
    return (<>
        <SeverityTagRenderer severity="critical" count={severity.Critical} hideNull={hideNull} />
        <SeverityTagRenderer severity="high" count={severity.High} hideNull={hideNull} />
        {showMedium && <SeverityTagRenderer severity="medium" count={severity.Medium} hideNull={hideNull} />}
        {showLow && <SeverityTagRenderer className="m-r-0" severity="low" count={severity.Low} hideNull={hideNull} />}
    </>)
}

function SeverityTagRenderer({severity = '', className = '', count, hideNull}) {
    return hideNull && _.isNull(count) ? null : <Tag className={`${lowerCase(severity) } severity-tag ${className}`}><Text className="finding-text">{(count) || 0}</Text>&nbsp;{getVocabulary(severity)[0]}&nbsp;</Tag>
}

export const renderTrend = ({
    severityKey, 
    oldSeverity = {}, 
    severity, 
    totalCount, 
    newTrendDataFormat = false, 
    hideTrend = false, 
    showTrendTooltips = false, 
    trendDayDiff = 30
}) => {
    let trendValue;
    let content;
    if (hideTrend) return null;
    if (newTrendDataFormat) {
        trendValue = severity[`${severityKey.toLowerCase()}Trend`] || 0;
    } else {
        if (_.isUndefined(oldSeverity)) {
            return null;
        }
        let newVal;
        let oldVal;
        if (severityKey !== 'total') {
            newVal = severity[capitalize(severityKey)] || 0;
            oldVal = oldSeverity[capitalize(severityKey)] || 0;
        } else {
            newVal = totalCount(severity);
            oldVal = totalCount(oldSeverity);
        }
        trendValue = newVal - oldVal;
    }

    if(showTrendTooltips){
        trendValue = getTrendTooltipCount(severityKey, severity);
    }

    if(trendValue === 0){
        content = 'No change';
     }
     else if(trendValue > 0){
        content = `+${trendValue} in last ${trendDayDiff} days`;
     }
     else{
        content = `${trendValue} in last ${trendDayDiff} days`;
     }

    return <span className="m-r-sm">{content}</span>;
};

const getTrendTooltipCount = (severityKey, severity)=>{
    if(!severityKey || _.isEmpty(severity)){
        return;
    }
    let trendValue;
    switch (severityKey) {
        case 'Critical':
            trendValue = severity.prevCritical !== undefined ? severity[severityKey] - severity.prevCritical : undefined;
            break;
        case 'High':
            trendValue = severity.prevHigh !== undefined ? severity[severityKey] - severity.prevHigh : undefined;
            break;
        case 'Medium':
            trendValue = severity.prevMedium !== undefined ? severity[severityKey] - severity.prevMedium : undefined;
            break;
        case 'Low':
            trendValue = severity.prevLow !== undefined ? severity[severityKey] - severity.prevLow : undefined;
            break;
        case 'Total':
            trendValue = severity.prevTotal !== undefined ? severity.total - severity.prevTotal : undefined;
            break;
        default:
            break;    
    }
    return trendValue;
}

function ProgressRenderer(percent){
    if(percent === -1){
        return (<Progress type="circle" width={PROGRESS_COLOR['width']} strokeWidth={12} strokeColor={{'0%': PROGRESS_COLOR['error'],'100%': PROGRESS_COLOR['error']}} percent={100} status="exception"/>)    
    }
    return (<Progress type="circle" width={PROGRESS_COLOR['width']} strokeWidth={12} strokeColor={{'0%': PROGRESS_COLOR[0],'100%': PROGRESS_COLOR[100]}} percent={percent}/>)
}

function RelativeTimeRenderer(value){
    if(!value){
        return 'N/A'
    }
    return <RelativeTimeComponent value={value} />
}

function TotalTimeTaken(startTime, endTime){
    if(!startTime || !endTime){
        return 'N/A'
    }
    
    const start = moment(startTime);
    const end = moment(endTime);
    const difference = end.diff(start, 'milliseconds');

    return moment().add(difference, 'milliseconds').fromNow(true)
}

function TimeRenderer(value){
    if(!value){
        return 'N/A'
    }

    const parsedTime = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!parsedTime.isValid()) {
        return 'N/A';
    }

    const machineReadable = parsedTime.format('YYYY-MM-DDTHH:mm:ssZ');
    const humanReadable = parsedTime.format('MM/DD/YYYY hh:mm A');
    
    return (
        <time
            dateTime={machineReadable}>
            {humanReadable}
        </time>
    );
}

function DateRenderer(value) {
  if (!value) {
    return 'N/A';
  }
  if (_.isString(value)) {
    const numValue = +value;
    if (!isNaN(numValue)) {
      value = numValue;
    }
  }

  if (moment(value).isValid()) {
    return moment(value).format('MM/DD/YYYY');
  }
  return value;
}

function NameIdMapRenderer(object = {}){
    const name = _.get(object, 'name', object);
    if(!name || typeof name !== 'string'){
        return <div>N/A</div>;
    }
    return name;
}

function StatusDotRenderer(status){
    return (
        <div 
            style={{
                width: 15, 
                height: 15, 
                backgroundColor: (status === 'success' ? COLORS.success : status === 'default' ? COLORS.default : SEVERITY_COLOR.critical),
                borderRadius: '50%'
            }}
        />
    )
}

function AgeRenderer(time){
    if(!time){
        return CellRenderers.VALUE_OR_NA();
    }
    return <RelativeTimeComponent value={time} suffix={false} />
}

function VocabularyRenderer(value){
    const name = _.isObject(value) ? value.name : value;
    if(name){
        const vocabName = VOCABULARY_STATUS.includes(GLOBAL_STATUS[name]) ? GLOBAL_STATUS[name] : name.toLowerCase();
        return getVocabulary(vocabName)
    }
    return 'N/A'
    
}

export default function EllipsisCell(text){
	return (
        <div className="ellipsis-cell" style={{width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} >
            <Tooltip title={text}>
                {text}
            </Tooltip>
		</div>
	)
}

const nameWithInitialCircle = (name) => <div className="d-flex align-items-center"><NameAvatar name={name} /><div className="m-l-xs">{name}</div></div>;

function RenderHtmlEditor(value) {
    return <div className="message">{parseHtml(marked.parse(CellRenderers.VALUE_OR_NA(value)), { transform: transformComment })}</div>
}

function renderSingleLineTags(tags){
    if (isEmpty(tags)) {
        return CellRenderers.VALUE_OR_NA();
    }
    return (
        <div className="d-flex">
            <div
                className="text-ellipsis"
                style={{ marginRight: 4 }}
                title={tags[0]}
            >
                {tags[0]}
            </div>
            {tags?.length > 1 ? (
                <Popover
                    content={
                        <ul className="m-b-0 p-l-m">
                            {tags.slice(1, tags.length).map((t) => (
                                <li key={t}>{t}</li>
                            ))}
                        </ul>
                    }
                >
                    <Tag>+{tags.length - 1}</Tag>
                </Popover>
            ) : null}
        </div>
    );
}

export const CellRenderers = {
    TAGS: TagsRenderer,
    PLUS_MORE: MoreItemsRenderer,
    PLUS_MORE_SINGLE_LINE: PlusMoreSingleLineRenderer,
    TAGS_AS_FILTER: findingTags,
    SEVERITY: SeverityRenderer,
    CUSTOMIZED_SEVERITY: CustomizedSeverityRenderer,
    AllFinding:TotalFindingRenderer,
    SEVERITY_TAGS: TotalFindings,
    SUB_PRODUCT: SubProductRenderer,
    Progress:ProgressRenderer,
    TIME_RELATIVE: RelativeTimeRenderer,
    TIME: TimeRenderer,
    DATE: DateRenderer,
    NAME_ID_MAP: NameIdMapRenderer,
    NAME_ID_MAP_LIST: (list = []) => {
        if(_.isEmpty(list)){
            return CellRenderers.VALUE_OR_NA(list);
        }
        return list.map((item, i) => <NameIdMapRenderer {...item} key={i} />)
    },
    STATUS_DOT: StatusDotRenderer,
    VALUE_OR_NA: (value) => (_.isEmpty(value) && !_.isNumber(value)) ||  value==='' || (['Not Available', 'n/a'].includes(value)) ? 'N/A' : value ,
    VALUE_OR_DASH: (value) =>
        (_.isEmpty(value) && !_.isNumber(value)) ||
        value === '' ||
        (['Not Available', 'n/a'].includes(value))
            ? NOT_AVAILABLE
            : value,
    VALUE_OR_BLANK: (value) => (_.isEmpty(value) && !_.isNumber(value)) ||  value==='' || (['Not Available', 'n/a'].includes(value)) ? '<Blank>' : value ,
    VALUE_OR_ZERO: (value) => (_.isEmpty(value) && !_.isNumber(value)) ? '0' : value,
    ELLIPSIS: EllipsisCell ,
    OPERATIONAL_STATUS: operationalStatusRenderer,
    SCAN_STATUS: scanStatusRenderer,
    RUNBOOK_STATUS: runbookStatusRenderer,
    AGE: AgeRenderer,
    NAME_WITH_INITIALS_CIRCLE: nameWithInitialCircle,
    VOCABULARY_RENDERER: VocabularyRenderer,
    TOTAL_TIME_TAKEN: TotalTimeTaken,
    HTML_EDITOR: RenderHtmlEditor,
    TAGS_SINGLE_LINE: renderSingleLineTags,
}

export const columnRenderArrayData = (data) =>{
    if(_.isEmpty(data)) {
        return CellRenderers.VALUE_OR_NA(data)
    }
    const renderData = data.split(',');
    const renderName = renderData.map((item, i) => <p key={i} className={i === renderData.length - 1 ? 'm-b-0' : 'm-b-xs'}>{item}</p>)
    return <ContentOverlay content={renderName} minHeight={40}/>
}
